import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  countUnseen,
  getLastSeen,
  listNotifications,
  newestTimestamp,
  setLastSeen,
} from "./notifications";
import { problemOf } from "./problems";

const REFRESH_MS = 120000;

const useNotifications = () => {
  const { user } = useAuth();
  const userId = user.id;
  const [state, setState] = useState({
    status: "loading",
    items: [],
    problem: null,
  });
  const [lastSeen, setLastSeenState] = useState(() => getLastSeen(userId));

  const refresh = useCallback(async () => {
    try {
      const items = await listNotifications();
      setState({ status: "ready", items, problem: null });
    } catch (error) {
      // Keep showing what we already have; otherwise the bell stays quiet.
      setState((s) => ({
        ...s,
        status: s.items.length ? "ready" : "error",
        problem: problemOf(error),
      }));
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const markSeen = useCallback(() => {
    const newest = newestTimestamp(state.items);
    if (!newest) return;
    setLastSeen(userId, newest);
    setLastSeenState(newest);
  }, [state.items, userId]);

  return {
    ...state,
    lastSeen,
    hasUnseen: countUnseen(state.items, lastSeen) > 0,
    markSeen,
  };
};

export default useNotifications;
